/**
 * OpenUI ContactsPanel — flat live CRM list (click continues in chat).
 */

'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { useTriggerAction } from '@openuidev/react-lang'

import { Button } from '@/components/ui/button'
import { ChatbotUIContext } from '@/context/context'
import {
  openUIPanelClass,
  openUIRowClass,
  openUISubtitleClass,
  openUITitleClass
} from '@/lib/openui/chrome'
import { cn } from '@/lib/utils'

export type OpenUIContactsPanelProps = {
  title?: string
  workspaceId?: string | null
  limit?: number
  className?: string
}

type ContactRow = {
  id: string
  name: string
  email: string
  company: string
  status: string
}

export function OpenUIContactsPanel({
  title = 'Contacts',
  workspaceId: workspaceIdProp,
  limit = 20,
  className
}: OpenUIContactsPanelProps) {
  const { selectedWorkspace } = useContext(ChatbotUIContext)
  const triggerAction = useTriggerAction()
  const workspaceId = (workspaceIdProp || selectedWorkspace?.id || '').trim()
  const [rows, setRows] = useState<ContactRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!workspaceId) {
      setRows([])
      setError('Select a workspace to load CRM contacts.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        limit: String(Math.min(Math.max(limit, 1), 100))
      })
      const res = await fetch(`/api/crm/contacts?${params.toString()}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      })
      if (!res.ok) throw new Error(`Failed to load contacts (${res.status})`)
      const data = await res.json()
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.contacts)
          ? data.contacts
          : Array.isArray(data?.rows)
            ? data.rows
            : []
      setRows(
        list.slice(0, limit).map((item: Record<string, unknown>) => {
          const first = typeof item.first_name === 'string' ? item.first_name : ''
          const last = typeof item.last_name === 'string' ? item.last_name : ''
          const name =
            [first, last].filter(Boolean).join(' ').trim() ||
            (typeof item.name === 'string' ? item.name : 'Unknown contact')
          return {
            id: typeof item.id === 'string' ? item.id : String(item.id || ''),
            name,
            email: typeof item.email === 'string' ? item.email : '',
            company:
              typeof item.company === 'string'
                ? item.company
                : typeof (item.company as { name?: string } | null)?.name ===
                    'string'
                  ? String((item.company as { name: string }).name)
                  : '',
            status:
              typeof item.status === 'string'
                ? item.status
                : typeof item.lifecycle_stage === 'string'
                  ? item.lifecycle_stage
                  : ''
          }
        })
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load contacts')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [limit, workspaceId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (!workspaceId) {
    return (
      <div className={cn('px-1 py-3', openUISubtitleClass, className)}>
        Select a workspace to browse Nexus contacts.
      </div>
    )
  }

  return (
    <section
      className={cn(openUIPanelClass, className)}
      data-openui-contacts-panel="true"
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <h4 className={openUITitleClass}>{title}</h4>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => void refresh()}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
      {error ? (
        <p className="px-1 text-[12px] text-destructive">{error}</p>
      ) : null}
      {loading && rows.length === 0 ? (
        <div className="h-16 animate-pulse rounded-lg bg-foreground/[0.04]" aria-hidden="true" />
      ) : rows.length === 0 ? (
        <p className={cn('px-1 py-4 text-center', openUISubtitleClass)}>
          No contacts yet.
        </p>
      ) : (
        <ul>
          {rows.map(contact => (
            <li key={contact.id}>
              <button
                type="button"
                className={cn(openUIRowClass, 'flex-col items-start text-left')}
                onClick={() =>
                  triggerAction(
                    `Open CRM contact ${contact.name}${contact.email ? ` (${contact.email})` : ''} and summarize next steps`
                  )
                }
              >
                <span className="truncate text-[13px] font-medium text-foreground">
                  {contact.name}
                </span>
                <span className={cn('truncate', openUISubtitleClass)}>
                  {[contact.company, contact.email, contact.status]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
